import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking');
  const [plan, setPlan] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return undefined; }
    let stop = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API_URL}/api/billing/status/${sessionId}`, { credentials: 'include' });
        const d = await r.json();
        if (stop) return;
        if (d.payment_status === 'paid') {
          setState('paid');
          fetch(`${API_URL}/api/billing/me`, { credentials: 'include' }).then((x) => x.json()).then(setPlan).catch(() => {});
          return;
        }
        if (['expired', 'failed'].includes(d.payment_status)) { setState('error'); return; }
      } catch { /* retry */ }
      tries.current += 1;
      if (tries.current < 8) setTimeout(poll, 2000); else setState('timeout');
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="billing-success">
        {state === 'checking' && <><Loader2 className="w-12 h-12 text-[#006400] animate-spin mx-auto" /><h1 className="text-xl font-bold mt-4">Confirming your payment…</h1><p className="text-sm text-slate-500 mt-1">This only takes a moment.</p></>}
        {state === 'paid' && <><CheckCircle2 className="w-14 h-14 text-[#006400] mx-auto" /><h1 className="text-2xl font-bold mt-4" data-testid="billing-success-title">You're on {plan?.tier?.name || 'your new plan'}</h1>
          {plan?.tier?.id === 'marketing'
            ? <p className="text-sm text-slate-600 mt-1">One last step: tell us your ad budget, areas and goals so the team can start your campaigns straight away. Takes two minutes.</p>
            : <p className="text-sm text-slate-600 mt-1">Your website build and hosting are included — order it from the Websites page whenever you're ready. Thanks for supporting ProPortal.</p>}
          <div className="flex gap-2 mt-6"><Button variant="outline" className="flex-1" onClick={() => navigate('/billing')} data-testid="billing-success-plan">View plan</Button>
            {plan?.tier?.id === 'marketing'
              ? <Button className="flex-1 bg-[#006400] text-white" onClick={() => navigate('/marketing/onboarding')} data-testid="billing-success-marketing">Start marketing brief</Button>
              : <Button className="flex-1 bg-[#006400] text-white" onClick={() => navigate('/calendar')} data-testid="billing-success-calendar">Open calendar</Button>}</div></>}
        {(state === 'timeout' || state === 'error') && <><AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" /><h1 className="text-xl font-bold mt-4">{state === 'timeout' ? "Still processing" : "Payment not completed"}</h1>
          <p className="text-sm text-slate-600 mt-1">{state === 'timeout' ? "Your bank is taking a little longer. Check your plan in a minute — you won't be charged twice." : "No charge was made. You can try again from the plan page."}</p>
          <Button className="mt-6 bg-[#006400] text-white" onClick={() => navigate('/billing')} data-testid="billing-success-back">Back to plan</Button></>}
      </div>
    </div>
  );
}
